import { memo, useCallback, useMemo } from 'react';
import { HStack, TextBlock, VStack } from '@library';
import type { PostsCategory } from '../../../queries/usePostsCategoriesWithCount';
import usePostsCategoriesWithCount from '../../../queries/usePostsCategoriesWithCount';
import { ProgressSpinner } from '../../../../core/components/ProgressIndicators';
import Checkbox from '../../../../core/components/input/Checkbox';
import styles from './CategoryFilter.module.css';

const CategoryItem = memo(
    ({
        category,
        selected,
        onChange,
    }: {
        category: PostsCategory;
        selected: boolean;
        onChange: (key: string, value: boolean) => void;
    }) => {
        return (
            <Checkbox
                truncateLabel
                value={category.id}
                checked={selected}
                label={
                    <div title={category.name}>
                        <TextBlock secondary regular>
                            <HStack
                                justify="spaceBetween"
                                gap="small"
                                align="center"
                            >
                                <span>{category.name}</span>

                                <span className={styles.checkboxTotal}>
                                    {category.total}
                                </span>
                            </HStack>
                        </TextBlock>
                    </div>
                }
                onChange={e => {
                    onChange(category.id, e.target.checked);
                }}
            />
        );
    }
);

const CategoryFilter = ({
    values,
    onChange,
}: {
    values: string[];
    onChange: (values: string[]) => void;
}) => {
    const categories = usePostsCategoriesWithCount();

    const valuesMap = useMemo(
        () =>
            categories?.reduce(
                (res, category) => {
                    return {
                        ...res,
                        [category.id]: values.includes(category.id),
                    };
                },
                {} as Record<string, boolean>
            ) || {},
        [categories, values]
    );

    const handleChange = useCallback(
        (key: string, value: boolean) => {
            if (value) {
                onChange([...values, key]);
            } else {
                const array = values.splice(0);
                array.splice(array.indexOf(key), 1);
                onChange(array);
            }
        },
        [values, onChange]
    );

    return (
        <VStack gap="micro">
            {categories ? (
                categories.map(category => (
                    <CategoryItem
                        category={category}
                        selected={valuesMap[category.id]}
                        onChange={handleChange}
                        key={category.id}
                    />
                ))
            ) : (
                <ProgressSpinner />
            )}
        </VStack>
    );
};

export default memo(CategoryFilter);
